import { useDispatch, useSelector } from '@redux';
import crudActions from '@redux/crudActions';
import crudSelectors from '@redux/crudSelectors';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import CustomBreadcrumb from 'components/commons/Breadcrumb';
import { useRestContext } from 'components/form/RestInputContext';
import CreateActions from 'components/rest/CreateActions';
import ButtonRow from 'components/rest/FooterButtonRow';
import RestForm from 'components/rest/RestForm';
import _, { zipObjectDeep } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DynamicObjectType } from 'types/commons';
import { getSearchParams } from 'utils/tools';

export interface IRestCreate {
  breadcrumbs?: ItemType[];
  showModal?: boolean;
  children?: React.ReactNode;
  model: string;
  formatOnSubmit?: (
    values: DynamicObjectType,
    record?: DynamicObjectType,
  ) => void;
  hasSelectFilter?: boolean;
  customPathAfterSubmit?: string;
  customSubmit?: (form) => void;
  customOnBack?: (data, dataSubmit) => void;
  customUrlBack?: string;
  handleSubmitFailed?: (values: DynamicObjectType) => void;
  apiOptions?: DynamicObjectType;
  customApiSubmit?: any;
}

function RestCreateContent({
  model,
  breadcrumbs,
  showModal,
  children,
  hasSelectFilter,
  customSubmit,
  customUrlBack,
}: IRestCreate) {
  const location = useLocation();
  const { form } = useRestContext();
  const defaultBreadcrumbs = useMemo(
    () =>
      breadcrumbs || [
        {
          title: `${model}.listTitle`,
          href: `/${model}`,
        },
        {
          title: `${model}.createTitle`,
        },
      ],
    [breadcrumbs, model],
  );

  useEffect(() => {
    const urlSearchParams = getSearchParams(location.search);
    form.setFieldsValue(urlSearchParams);
  }, [location.search]);

  return (
    <>
      {showModal ? (
        <>
          <div className="showModal-content">{children}</div>
          <ButtonRow model={model} type="create" />
        </>
      ) : (
        <>
          <div className="d-flex space-between">
            <CustomBreadcrumb items={defaultBreadcrumbs} />
            <CreateActions
              model={model}
              hasSelectFilter={hasSelectFilter}
              customSubmit={customSubmit}
              customUrlBack={customUrlBack}
            />
          </div>
          {children}
        </>
      )}
    </>
  );
}

export default ({
  customSubmit,
  customOnBack,
  handleSubmitFailed,
  apiOptions,
  customApiSubmit,
  ...props
}: IRestCreate) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const record = useSelector((state) =>
    crudSelectors[props.model].getDefaultCreateData(state, {
      location,
    }),
  );

  const handleCreate = (value: DynamicObjectType) => {
    const parseToObj = zipObjectDeep(Object.keys(value), _.values(value));
    const dataSubmit = props.formatOnSubmit
      ? props.formatOnSubmit(parseToObj)
      : parseToObj;
    if (customApiSubmit) {
      dispatch(
        customApiSubmit({
          data: dataSubmit,
          options: apiOptions,
        }),
      ).then(({ payload: { data } }) => {
        if (customOnBack) {
          customOnBack(data, dataSubmit);
        } else {
          data?.id &&
            navigate(
              props?.customPathAfterSubmit
                ? `/${props?.customPathAfterSubmit}`
                : `/${props.model}/${data?.id}/show`,
            );
        }
      });
    } else {
      dispatch(
        crudActions[props.model].create({
          data: dataSubmit,
          options: apiOptions,
        }),
      ).then(({ payload: { data } }) => {
        if (customOnBack) {
          customOnBack(data, dataSubmit);
        } else {
          data?.id &&
            navigate(
              props?.customPathAfterSubmit
                ? `/${props?.customPathAfterSubmit}`
                : `/${props.model}/${data?.id}/show`,
            );
        }
      });
    }
  };

  return (
    <RestForm
      handleSubmitFailed={handleSubmitFailed}
      onSubmit={handleCreate}
      isEdit={false}
      record={record}
    >
      <RestCreateContent customSubmit={customSubmit} {...props} />
    </RestForm>
  );
};
